import { z } from 'zod';
import { isoDate, uuid } from './schemas.js';

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((s) => (s === '' ? null : s))
    .nullable()
    .optional();

export const PROJECT_STATUSES = ['planned', 'active', 'on_hold', 'completed'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const projectSchema = z
  .object({
    name: z.string().trim().min(1, 'Enter the project name').max(120),
    code: text(30),
    client: text(120),
    location: text(120),
    status: z.enum(PROJECT_STATUSES).default('active'),
    startDate: isoDate.nullable().optional(),
    endDate: isoDate.nullable().optional(),
    description: text(2000),
  })
  .strict()
  .refine((p) => !p.endDate || !p.startDate || p.endDate >= p.startDate, { message: 'End date must be after the start date', path: ['endDate'] });

export const projectMemberSchema = z
  .object({
    employeeId: uuid,
    role: text(80),
    fromDate: isoDate,
    toDate: isoDate.nullable().optional(),
  })
  .strict()
  .refine((m) => !m.toDate || m.toDate >= m.fromDate, { message: 'End date must be after the start date', path: ['toDate'] });

export interface ProjectMemberDto {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeCode: string;
  position: string | null;
  role: string | null;
  fromDate: string;
  toDate: string | null;
  current: boolean;
}

export interface ProjectDto {
  id: string;
  name: string;
  code: string | null;
  client: string | null;
  location: string | null;
  status: ProjectStatus;
  startDate: string | null;
  endDate: string | null;
  description: string | null;
  /** People on the project today. */
  memberCount: number;
  /** Current and past members the viewer may see. */
  members: ProjectMemberDto[];
}

// ---------------------------------------------------------------------------
// Project time tracking (hours logged against a project / sub-job)
// ---------------------------------------------------------------------------
export const projectTimeEntrySchema = z
  .object({
    projectId: uuid,
    employeeId: uuid,
    workDate: isoDate,
    hours: z.number().positive().max(24),
    subJob: text(120),
    note: text(500),
  })
  .strict();

export interface ProjectTimeEntryDto {
  id: string;
  projectId: string;
  projectName: string;
  employeeId: string;
  employeeName: string;
  employeeCode: string;
  workDate: string;
  hours: number;
  subJob: string | null;
  note: string | null;
}

/** One row of the per-project hours report (a project, or one of its sub-jobs). */
export interface ProjectHoursRowDto {
  projectId: string;
  projectName: string;
  projectCode: string | null;
  subJob: string | null;
  totalHours: number;
  entries: number;
  people: { employeeId: string; employeeName: string; employeeCode: string; hours: number; entries: number }[];
}

export interface ProjectHoursReportDto {
  from: string;
  to: string;
  rows: ProjectHoursRowDto[];
  totalHours: number;
}

// ---------------------------------------------------------------------------
// Internal policy page
// ---------------------------------------------------------------------------
export const policySchema = z
  .object({
    title: z.string().trim().min(1, 'Enter a title').max(150),
    /** Plain text: blank lines separate paragraphs, "# " starts a heading, "- " a bullet. */
    body: z.string().max(100_000),
    fileId: uuid.nullable().optional(),
    /** Tell everyone that the policy changed. */
    notify: z.boolean().default(false),
  })
  .strict();

export interface PolicyDto {
  version: number;
  title: string;
  body: string;
  file: { id: string; filename: string } | null;
  publishedBy: string;
  publishedAt: string;
}
